import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';
import { ToastService } from 'src/app/services/toast.service';
import { FoodService } from 'src/app/services/food.service';
import { FoodOptionService } from 'src/app/services/food-option.service';

import { FoodOption } from 'src/app/interfaces/food-option';
import { User } from 'src/app/interfaces/user';
import { Meal } from 'src/app/interfaces/diet';
import { Food } from 'src/app/interfaces/food';
import { Macros } from 'src/app/interfaces/macros';

@Component({
  selector: 'app-food-options-form-modal',
  templateUrl: './food-options-form-modal.component.html',
  styleUrls: ['./food-options-form-modal.component.scss'],
})
export class FoodOptionsFormModalComponent implements OnInit {
  @ViewChild('foodOptionFormModal') dietModal: ElementRef | undefined;

  @Input() meal?: Meal;

  @Output() foodOptionCreated = new EventEmitter<FoodOption>();
  @Output() foodOptionUpdated = new EventEmitter<FoodOption>();

  editing: boolean = false;
  showPortionReferenceType: boolean = true;

  user?: User;
  selectedUser?: User;
  foods?: Food[];

  selectedFoodOption?: FoodOption;
  selectedFood?: Food;
  selectedFoodMacros?: Macros;

  foodOptionForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private storageService: StorageService,
    private stateService: StateService,
    private foodService: FoodService,
    private foodOptionService: FoodOptionService
  ) {
    this.foodOptionForm = this.fb.group({
      foodName: ['', [Validators.required]],
      portion: [{ value: '', disabled: true }, [Validators.required]],
      portionReferenceType: [''],
    });
  }

  ngOnInit(): void {
    this.user = this.storageService.get('staffUser');
    this.selectedUser = this.storageService.get('selectedUser');

    this.getAllFoods();

    this.stateService.edition$.subscribe({
      next: (editing: boolean) => {
        this.editing = editing;

        if (editing && this.stateService.selectedFoodOption) {
          this.selectedFoodOption = this.stateService.selectedFoodOption;

          const selectedFood = this.foods?.filter(
            (food: Food) => food.id == this.selectedFoodOption?.food!.id
          )[0];

          this.portion.enable();

          this.foodName.setValue(selectedFood?.name);
          this.portion.setValue(this.selectedFoodOption!.portion);

          this.selectFoodFromInput();
        }
      },
    });

    const foodOptionModalHTMLElement = document.getElementById(
      'foodOptionsFormModal'
    );

    foodOptionModalHTMLElement?.addEventListener('shown.bs.modal', () => {
      if (this.stateService.selectedFoodGroup) {
        this.portionReferenceType.setValue('');
        this.showPortionReferenceType = false;
      } else {
        this.showPortionReferenceType = true;
      }
    });
  }

  onSubmit() {
    this.editing ? this.editFoodOption() : this.createFoodOption();
  }

  selectFoodFromInput() {
    const filteredFood = this.foods?.filter(
      (food: Food) => food.name == this.foodName.value
    );

    if (filteredFood!.length > 0) {
      this.selectedFood = filteredFood![0];
      this.portion.enable();

      this.selectedFoodMacros = {
        proteins: this.selectedFood.proteins * this.portion.value,
        carbohydrates: this.selectedFood.carbohydrates * this.portion.value,
        fats:
          (this.selectedFood.saturatedFats +
            this.selectedFood.unsaturatedFats) *
          this.portion.value,
        fiber: this.selectedFood.fiber * this.portion.value,
        kcal: parseInt(this.selectedFood.kcal) * this.portion.value,
      };

      if (this.selectedFood.portionMeasure == '100GR') {
        for (const key in this.selectedFoodMacros) {
          if (this.selectedFoodMacros.hasOwnProperty(key)) {
            this.selectedFoodMacros[key as keyof Macros] =
              (this.selectedFoodMacros[key as keyof Macros] as number) / 100;
          }
        }
      }
    } else {
      this.selectedFood = undefined;
      this.selectedFoodMacros = undefined;
      this.portion.disable();
    }
  }

  createFoodOption() {
    // FoodId definition
    let foodId: Food[] | number | undefined = this.foods?.filter(
      (food: Food) => food.name == this.foodName.value
    );

    if (foodId) {
      foodId = foodId[0].id;
    } else {
      this.toastService.alert(
        'No hemos encontrado la comida que escribiste, porfavor selecciona alguna de la lista. (empieza a escribir y apareceran las comidas disponibles)'
      );

      return;
    }

    // group definition
    let group;
    if (this.meal!.foodOptions.length > 0) {
      group = (this.meal?.foodOptions!.at(-1)?.at(-1)?.group as number) + 1;
    } else {
      group = 1;
    }

    // isDefault definition
    let isDefault = true;

    if (this.stateService.selectedFoodGroup) {
      group = this.stateService.selectedFoodGroup;
      isDefault = false;
    }

    const foodOption: FoodOption = {
      foodId: foodId as number,
      portion: this.portion.value,
      group: group as number,
      mealId: this.meal!.id,
      isDefault,
      portionReferenceType:
        this.foodOptionForm.controls['portionReferenceType'].value, // Change this line
    };

    this.foodOptionService.createFood(foodOption).subscribe({
      next: () => {
        this.stateService.selectedFoodGroup = undefined;
        this.stateService.selectedFoodOption = undefined;
        this.stateService.openModal.hide();
        this.clearForm();
      },
    });
  }

  editFoodOption() {
    // FoodId definition
    let foodId: Food[] | number | undefined = this.foods?.filter(
      (food: Food) => food.name == this.foodName.value
    );

    if (foodId) {
      foodId = foodId[0].id;
    } else {
      this.toastService.alert(
        'No hemos encontrado la comida que escribiste, porfavor selecciona alguna de la lista. (empieza a escribir y apareceran las comidas disponibles)'
      );

      return;
    }

    const foodOption: FoodOption = {
      ...this.selectedFoodOption!,
      foodId: foodId as number,
      portion: this.portion.value,
      portionReferenceType:
        this.foodOptionForm.controls['portionReferenceType'].value, // Change this line
    };

    this.foodOptionService.updateFood(foodOption).subscribe({
      next: () => {
        this.stateService.selectedFoodGroup = undefined;
        this.stateService.selectedFoodOption = undefined;
        this.stateService.updateEdition(false);
        this.clearForm();
        this.stateService.openModal.hide();
      },
    });
  }

  clearForm() {
    this.stateService.selectedFoodGroup = undefined;
    this.foodName.setValue('');
    this.portion.setValue('');

    this.foodOptionForm.reset();

    this.portionReferenceType.setValue('');
  }

  getAllFoods() {
    this.foodService.getAllFoods().subscribe({
      next: (response: any) => {
        this.foods = response;

        this.foods!.forEach((food: Food) => {
          food.totalFats = food.saturatedFats + food.unsaturatedFats;
        });
      },
    });
  }

  onClose() {
    this.selectedFood = undefined;
    this.selectedFoodMacros = undefined;
    this.clearForm();
  }

  get foodName() {
    return this.foodOptionForm.controls['foodName'];
  }

  get portion() {
    return this.foodOptionForm.controls['portion'];
  }

  get portionReferenceType() {
    return this.foodOptionForm.controls['portionReferenceType'];
  }
}
